import { aawzDate } from '@utils/dates/aawz-date'
import {
  STATUS_CODES,
  STATUS_DESCRIPTORS,
  REFUSAL_EVENT_NAMES,
  CANCEL_EVENT_NAMES,
  CLICKSIGN_PROVIDER_ID,
  TERMINAL_NEGATIVE_STATUS_CODES,
} from '@modules/documents/constants/contract-status-constants'

const SIGNED_CODES = new Set([
  STATUS_CODES.SIGNED_COMPLETE,
  STATUS_CODES.FALLBACK_SIGNED,
])

const TERMINAL_NEGATIVE_SET = new Set(TERMINAL_NEGATIVE_STATUS_CODES)

/**
 * Pure derivation: maps a document payload to a status descriptor describing
 * exactly which of the 5 finalization paths the contract took (or one of the
 * 3 fallback states for non-Clicksign / external_signature documents).
 *
 * Defensive against null/missing fields; never throws; always returns a valid
 * descriptor of shape { code, label, color, icon, tooltip }.
 */
export function deriveContractStatus(document) {
  if (!document) return _buildDescriptor(STATUS_CODES.AWAITING_SIGNATURES, {})

  const base = _deriveBaseStatus(document)

  if (SIGNED_CODES.has(base.code) && _isValidityExpired(document)) {
    return _buildDescriptor(STATUS_CODES.EXPIRED_BY_VALIDITY, {
      data_fim_vigencia: _formatVigenciaDate(document.data_fim_vigencia),
    })
  }

  return base
}

/**
 * From a list of formatted contracts (each carrying `_status.code`), picks the
 * one that best represents the client's contract situation.
 *
 * Algorithm:
 * 1. Sort by most-recent first (`data_registro`, falling back to `data_atualizacao`).
 * 2. Walk the sorted list; return the first contract whose `_status.code` is
 *    NOT a terminal-negative state (deadline/refusal/cancel/vigência).
 * 3. If every contract is terminal-negative, return the most recent one.
 *
 * Why: a client with one expired-by-deadline contract AND one signed contract
 * should be displayed as "Concluído" — not "Encerrado" just because the latest
 * registration date happens to be a dead contract.
 */
export function pickRepresentativeContract(contracts) {
  if (!Array.isArray(contracts) || contracts.length === 0) return null

  const sorted = [...contracts].sort(
    (a, b) => _resolveContractDateMs(b) - _resolveContractDateMs(a)
  )

  const firstValid = sorted.find(
    (contract) =>
      contract &&
      contract._status &&
      !TERMINAL_NEGATIVE_SET.has(contract._status.code)
  )

  return firstValid || sorted[0]
}

function _deriveBaseStatus(document) {
  const rawStatus = document.status
  const config = document.config || {}
  const events = Array.isArray(config.events) ? config.events : []

  if (rawStatus === 'canceled') {
    return _buildDescriptor(
      STATUS_CODES.CANCELED_MANUAL,
      _buildCancelTooltipData({ document, events })
    )
  }

  if (!_isClicksign(document)) {
    return _buildDescriptor(_pickFallbackCode(rawStatus), {})
  }

  return _deriveClicksignFinalization({ document, config, events, rawStatus })
}

function _isValidityExpired(document) {
  const raw = document.data_fim_vigencia
  if (!raw) return false
  const fimVigencia = _parseVigenciaDate(raw)
  if (!fimVigencia || !fimVigencia.isValid()) return false
  const expiryMs = fimVigencia.endOf('day').valueOf()
  const nowMs = aawzDate().valueOf()
  if (!Number.isFinite(expiryMs) || !Number.isFinite(nowMs)) return false
  return expiryMs < nowMs
}

function _parseVigenciaDate(raw) {
  if (typeof raw !== 'string') {
    const direct = aawzDate(raw)
    return direct && direct.isValid() ? direct : null
  }
  const formats = ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY HH:mm']
  for (const format of formats) {
    const parsed = aawzDate(raw, format)
    if (parsed && parsed.isValid()) return parsed
  }
  const fallback = aawzDate(raw)
  return fallback && fallback.isValid() ? fallback : null
}

function _formatVigenciaDate(raw) {
  const parsed = _parseVigenciaDate(raw)
  if (!parsed) return ''
  return parsed.format('DD/MM/YYYY')
}

function _resolveContractDateMs(contract) {
  if (!contract || typeof contract !== 'object') return 0
  const dateFields = ['data_registro', 'data_atualizacao']
  for (const field of dateFields) {
    const value = contract[field]
    if (!value) continue
    const iso = String(value).replace(
      /(\d{2})\/(\d{2})\/(\d{4})/,
      '$3-$2-$1'
    )
    const ms = new Date(iso).getTime()
    if (Number.isFinite(ms)) return ms
  }
  return 0
}

function _deriveClicksignFinalization({ document, config, events, rawStatus }) {
  const refusalEvent = _findRefusalEvent(events)

  if (refusalEvent && config.block_after_refusal === true) {
    return _buildDescriptor(
      STATUS_CODES.CLOSED_BY_REFUSAL,
      _buildRefusalTooltipData({ refusalEvent })
    )
  }

  if (_isClosedByDeadline({ document, config, refusalEvent })) {
    return _buildDescriptor(STATUS_CODES.CLOSED_BY_DEADLINE, {
      deadline_at: _formatIsoDate(config.deadline_at),
    })
  }

  if (rawStatus === 'running' || rawStatus === 'processing') {
    return _buildDescriptor(
      STATUS_CODES.AWAITING_SIGNATURES,
      _buildAwaitingTooltipData({ config })
    )
  }

  return _buildDescriptor(STATUS_CODES.SIGNED_COMPLETE, {
    data_finalizacao: _formatIsoDate(
      config.finished_at || document.data_atualizacao
    ),
  })
}

function _isClicksign(document) {
  if (document.external_signature === true) return false
  return document.provider_id === CLICKSIGN_PROVIDER_ID
}

function _pickFallbackCode(rawStatus) {
  if (rawStatus === 'signed' || rawStatus === 'closed') {
    return STATUS_CODES.FALLBACK_SIGNED
  }
  if (rawStatus === 'canceled') return STATUS_CODES.FALLBACK_CANCELED
  return STATUS_CODES.FALLBACK_PENDING
}

function _findRefusalEvent(events) {
  for (const event of events) {
    if (event && REFUSAL_EVENT_NAMES.includes(event.name)) return event
  }
  return null
}

function _findCancelEvent(events) {
  for (const event of events) {
    if (event && CANCEL_EVENT_NAMES.includes(event.name)) return event
  }
  return null
}

function _isClosedByDeadline({ document, config, refusalEvent }) {
  if (config.auto_close !== true) return false
  const deadlineMs = aawzDate(config.deadline_at).valueOf()
  if (!Number.isFinite(deadlineMs)) return false
  // Foi finalizado dentro do prazo → assinado por todos, não encerramento.
  if (config.finished_at) {
    const finishedMs = aawzDate(config.finished_at).valueOf()
    if (Number.isFinite(finishedMs) && finishedMs <= deadlineMs) return false
  }
  // Fallback: backend pode não popular config.finished_at. Se todos os
  // signatários estão como 'signed' e a última assinatura ocorreu dentro
  // do prazo, o documento foi concluído — não é encerramento por prazo.
  if (_wasFullySignedWithinDeadline({ document, deadlineMs })) return false
  const nowMs = aawzDate().valueOf()
  if (deadlineMs >= nowMs) return false
  if (refusalEvent) {
    const refusalMs = aawzDate(refusalEvent.occurred_at).valueOf()
    if (Number.isFinite(refusalMs) && refusalMs < deadlineMs) return false
  }
  return true
}

function _wasFullySignedWithinDeadline({ document, deadlineMs }) {
  const signatures = Array.isArray(document._signatures)
    ? document._signatures
    : []
  if (signatures.length === 0) return false
  const allSigned = signatures.every(
    (sig) => sig && sig.status === 'signed'
  )
  if (!allSigned) return false
  const lastSignatureMs = _resolveLastSignatureMs(signatures)
  if (!Number.isFinite(lastSignatureMs)) return false
  return lastSignatureMs <= deadlineMs
}

function _resolveLastSignatureMs(signatures) {
  let max = -Infinity
  for (const sig of signatures) {
    if (!sig) continue
    const raw = sig.data_atualizacao || sig.data_registro
    const ms = _parseSignatureDateMs(raw)
    if (Number.isFinite(ms) && ms > max) max = ms
  }
  return max === -Infinity ? Number.NaN : max
}

function _parseSignatureDateMs(raw) {
  if (!raw) return Number.NaN
  if (typeof raw !== 'string') {
    const direct = aawzDate(raw)
    return direct && direct.isValid() ? direct.valueOf() : Number.NaN
  }
  // Detecta o formato pela estrutura da string antes de fazer parse —
  // `parseDate` faz fallback para `new Date(str)` quando o formato não bate,
  // e `new Date('10/02/2026 18:57')` é interpretado como MM/DD/YYYY em V8,
  // gerando data válida porém errada.
  const formatRules = [
    {
      regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/,
      format: 'DD/MM/YYYY HH:mm:ss',
    },
    { regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, format: 'DD/MM/YYYY HH:mm' },
    { regex: /^\d{2}\/\d{2}\/\d{4}$/, format: 'DD/MM/YYYY' },
    {
      regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/,
      format: 'YYYY-MM-DD HH:mm:ss',
    },
    { regex: /^\d{4}-\d{2}-\d{2}$/, format: 'YYYY-MM-DD' },
  ]
  for (const { regex, format } of formatRules) {
    if (!regex.test(raw)) continue
    const parsed = aawzDate(raw, format)
    if (parsed && parsed.isValid()) return parsed.valueOf()
  }
  // Fallback: ISO 8601 e demais formatos que `Date` entende nativamente.
  const fallback = aawzDate(raw)
  return fallback && fallback.isValid() ? fallback.valueOf() : Number.NaN
}

function _buildAwaitingTooltipData({ config }) {
  const signers = Array.isArray(config.signers) ? config.signers : []
  return {
    n_signatarios: signers.length > 0 ? String(signers.length) : '',
    deadline_at: _formatIsoDate(config.deadline_at),
  }
}

function _buildRefusalTooltipData({ refusalEvent }) {
  const data = refusalEvent && refusalEvent.data ? refusalEvent.data : {}
  const signerName =
    (data.signer && data.signer.name) || (data.user && data.user.name) || ''
  return {
    nome_signatario: signerName,
    data_recusa: _formatIsoDate(refusalEvent && refusalEvent.occurred_at),
  }
}

function _buildCancelTooltipData({ document, events }) {
  const cancelEvent = _findCancelEvent(events)
  const eventData = cancelEvent && cancelEvent.data ? cancelEvent.data : {}
  const userName =
    (eventData.user && eventData.user.name) ||
    (eventData.signer && eventData.signer.name) ||
    ''
  const cancelDate =
    (cancelEvent && cancelEvent.occurred_at) || document.data_atualizacao
  return {
    data_cancelamento: _formatIsoDate(cancelDate),
    usuario: userName,
  }
}

function _formatIsoDate(value) {
  if (!value) return ''
  const date = aawzDate(value)
  if (!date.isValid()) return ''
  return date.format('DD/MM/YYYY')
}

function _buildDescriptor(code, tooltipData) {
  const descriptor = STATUS_DESCRIPTORS[code]
  return {
    code,
    label: descriptor.label,
    color: descriptor.color,
    icon: descriptor.icon,
    tooltip: _interpolateTooltip(descriptor.tooltipTemplate, tooltipData),
  }
}

/**
 * Replaces `{placeholder}` tokens with values from `data`. If a segment
 * (period- or semicolon-delimited) contains an unresolved placeholder, the
 * entire segment is dropped. Separators are only kept between segments that
 * survive interpolation, so missing data never leaves trailing `;` or `.`.
 * The result always ends with a single `.` when non-empty, and never contains
 * literal `{`, `}`, `undefined`, or `null`.
 */
function _interpolateTooltip(template, data) {
  if (!template) return ''
  const segments = _splitIntoSegments(template)
  const usable = []
  for (const segment of segments) {
    const filled = _fillSegment(segment.text, data)
    if (filled === null) continue
    usable.push({ text: filled, separator: segment.separator })
  }
  if (usable.length === 0) return ''
  return _joinUsableSegments(usable).trim()
}

function _joinUsableSegments(usable) {
  let result = ''
  for (let i = 0; i < usable.length; i += 1) {
    const isLast = i === usable.length - 1
    result += usable[i].text
    result += isLast ? '.' : usable[i].separator || ';'
  }
  return result
}

function _splitIntoSegments(template) {
  const segments = []
  let buffer = ''
  for (let i = 0; i < template.length; i += 1) {
    const ch = template[i]
    if (ch === '.' || ch === ';') {
      segments.push({ text: buffer, separator: ch })
      buffer = ''
      continue
    }
    buffer += ch
  }
  if (buffer.length > 0) segments.push({ text: buffer, separator: '' })
  return segments
}

function _fillSegment(segment, data) {
  let result = segment
  const placeholderRegex = /\{([a-z_]+)\}/g
  const placeholders = segment.match(placeholderRegex) || []
  for (const placeholder of placeholders) {
    const key = placeholder.slice(1, -1)
    const value = data[key]
    if (value === undefined || value === null || value === '') return null
    result = result.split(placeholder).join(String(value))
  }
  return result
}
